import { useEffect, useState } from "react";
import { MINUTE_MS } from "../logic/dayTime";

/**
 * Живият часовник на страницата: връща „сега" и се обновява в началото на
 * всяка минута (не на всеки 60 s от монтирането — иначе „19:10" би се сменило
 * чак в 19:10:59). Всеки тик се пренасочва към следващата граница на минута
 * (setTimeout, не setInterval), а при връщане към таба (visibilitychange /
 * focus) часовникът се засича веднага — браузърът приспива таймерите на
 * фонов таб и иначе „СЕГА" изостава с минути (освободена маса още изглежда
 * заета).
 *
 * `enabled` false — не тиктака и не слуша фокуса (екранът, който го държи,
 * не се прерисува всяка минута напразно); при включване се засича веднага.
 */
export const useNow = (enabled = true): number => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      clearTimeout(timeout);
      setNow(Date.now());
      timeout = setTimeout(tick, MINUTE_MS - (Date.now() % MINUTE_MS));
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    // Включен след пауза — засичане веднага, но само ако минутата е друга
    // (иначе същата стойност и без излишно прерисуване).
    setNow((previous) =>
      Math.floor(previous / MINUTE_MS) === Math.floor(Date.now() / MINUTE_MS) ? previous : Date.now(),
    );
    timeout = setTimeout(tick, MINUTE_MS - (Date.now() % MINUTE_MS));
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [enabled]);

  return now;
};
